// Formats d'affichage du handoff : jours (« 14 oct », « 14 → 22 oct », « mar. 6 oct · 6 h → 11 h »),
// délais relatifs (« dans 9 j », « encore 7 j », « demain »), frise J−7 → J+30, saisons, heures.
// Toujours à l'heure de Paris, comme le calcul des fenêtres.
import { daysBetween, type Category, type MyWindowDto, type Season } from '@crenow/shared';

const ZONE = 'Europe/Paris';
const DAY_KEY = new Intl.DateTimeFormat('en-CA', { timeZone: ZONE, year: 'numeric', month: '2-digit', day: '2-digit' });
const HOUR_KEY = new Intl.DateTimeFormat('en-GB', { timeZone: ZONE, hour: '2-digit', hourCycle: 'h23' });
const HHMM = new Intl.DateTimeFormat('fr-FR', { timeZone: ZONE, hour: '2-digit', minute: '2-digit' });
// Jours calendaires (AAAA-MM-JJ) lus en UTC : pas de décalage horaire possible.
const SHORT = new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'short', timeZone: 'UTC' });
const WEEKDAY = new Intl.DateTimeFormat('fr-FR', { weekday: 'short', timeZone: 'UTC' });
const MONTH = new Intl.DateTimeFormat('fr-FR', { month: 'long', timeZone: 'UTC' });

export const CATEGORY_LABELS: Record<Category, string> = { champignons: 'Champignons', peche: 'Pêche', jardin: 'Jardin' };

/** Jour local (Paris) d'un instant. */
export const parisDay = (instant: Date = new Date()) => DAY_KEY.format(instant);
export const parisHour = (instant: Date = new Date()) => Number(HOUR_KEY.format(instant));
/** « 09:42 ». */
export const hhmm = (iso: string) => HHMM.format(new Date(iso));

const utc = (day: string) => new Date(`${day}T00:00:00Z`);
/** « 14 oct », « 5 sept » (sans le point d'abréviation). */
export const shortDay = (day: string) => SHORT.format(utc(day)).replace(/\.$/, '');
/** « mar. 6 oct ». */
export const weekdayDay = (day: string) => `${WEEKDAY.format(utc(day))} ${shortDay(day)}`;
/** « août » pour `08-15`. */
export const monthName = (monthDay: string) => MONTH.format(utc(`2024-${monthDay}`));
export const seasonLabel = (season: Season) => `${monthName(season.from)} → ${monthName(season.to)}`;

type WindowLike = Pick<MyWindowDto, 'status' | 'start' | 'end' | 'startHour' | 'endHour' | 'season'>;

const isSlot = (window: WindowLike) => window.startHour !== null && window.endHour !== null;

/** Période de la fenêtre, en mono sur la carte. */
export function windowWhen(window: WindowLike): string {
  if (window.status === 'hors_saison') return window.season ? `saison ${seasonLabel(window.season)}` : 'hors saison';
  const { start, end } = window;
  if (!start || !end) return 'aucune fenêtre prévue';
  if (isSlot(window)) {
    return start === end
      ? `${weekdayDay(start)} · ${window.startHour} h → ${window.endHour} h`
      : `${weekdayDay(start)} ${window.startHour} h → ${weekdayDay(end)} ${window.endHour} h`;
  }
  if (window.status === 'en_cours') return `jusqu’au ${shortDay(end)}`;
  if (start === end) return shortDay(start);
  if (start.slice(0, 7) === end.slice(0, 7)) return `${Number(start.slice(8))} → ${shortDay(end)}`;
  return `${shortDay(start)} → ${shortDay(end)}`;
}

/** Délai relatif à aujourd'hui (jour de Paris). */
export function windowRelative(window: WindowLike, today: string): string {
  const { start, end } = window;
  if (window.status === 'hors_saison' || !start || !end) return '';
  if (window.status === 'en_cours') {
    if (isSlot(window)) return 'en ce moment';
    const left = daysBetween(today, end);
    return left <= 0 ? 'dernier jour' : `encore ${left} j`;
  }
  if (window.status === 'termine') {
    const ago = daysBetween(end, today);
    return ago <= 0 ? 'aujourd’hui' : ago === 1 ? 'hier' : `il y a ${ago} j`;
  }
  const until = daysBetween(today, start);
  return until <= 0 ? 'aujourd’hui' : until === 1 ? 'demain' : `dans ${until} j`;
}

export const TIMELINE_PAST = 7;
export const TIMELINE_DAYS = 37;

/** Segment de la mini-frise J−7 → J+30, en % de la piste ; `null` sans fenêtre à montrer. */
export function timelineSegment(window: WindowLike, today: string): { left: number; width: number } | null {
  const { start, end } = window;
  if (window.status === 'hors_saison' || !start || !end) return null;
  const from = daysBetween(today, start) + (window.startHour ?? 0) / 24;
  const to = daysBetween(today, end) + (window.endHour !== null ? window.endHour / 24 : 1);
  const a = Math.max(from, -TIMELINE_PAST);
  const b = Math.min(to, TIMELINE_DAYS - TIMELINE_PAST);
  if (b <= a) return null;
  return { left: ((a + TIMELINE_PAST) / TIMELINE_DAYS) * 100, width: Math.max(1.4, ((b - a) / TIMELINE_DAYS) * 100) };
}

/** Instant passé, pour « Dernier usage » et la supervision : « il y a 4 min », « hier 18:02 », « 2 août ». */
export function sinceLabel(iso: string | null, now: Date = new Date()): string {
  if (!iso) return 'jamais';
  const instant = new Date(iso);
  const minutes = Math.floor((now.getTime() - instant.getTime()) / 60_000);
  if (minutes < 1) return 'à l’instant';
  if (minutes < 60) return `il y a ${minutes} min`;
  const days = daysBetween(parisDay(instant), parisDay(now));
  if (days === 0) return `il y a ${Math.floor(minutes / 60)} h`;
  if (days === 1) return `hier ${hhmm(iso)}`;
  if (days < 7) return `il y a ${days} j`;
  return shortDay(parisDay(instant));
}

export const thousands = (n: number) => n.toLocaleString('fr-FR');
/** Virgule décimale : « 1,2 ». */
export const decimal = (n: number) => String(n).replace('.', ',');
